import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';

/**
 * Captura errores inesperados de React y muestra una pantalla amigable.
 * Cuando DEBUG_MODE está activo expone el stack completo para facilitar el diagnóstico.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });

    console.error('Error capturado por ErrorBoundary', error, errorInfo);

    if (typeof this.props.onError === 'function') {
      this.props.onError(error, errorInfo);
    }
  }

  handleRetry = () => {
    if (typeof this.props.onReset === 'function') {
      try {
        this.props.onReset();
      } catch (resetError) {
        console.error('onReset lanzó un error dentro del ErrorBoundary', resetError);
      }
    }

    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleReload = () => {
    if (typeof window !== 'undefined' && window.location) {
      window.location.reload();
    }
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const debugMode = import.meta.env.VITE_DEBUG_MODE === 'true';


    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center px-6 py-12">
        <Card className="max-w-2xl w-full border-border shadow-lg">
          <CardHeader className="text-center space-y-3">
            <CardTitle className="text-3xl font-semibold">Algo no salió como esperábamos</CardTitle>
            <CardDescription className="text-base leading-relaxed text-muted-foreground">
              {debugMode
                ? 'DEBUG_MODE está activo. A continuación se muestran los detalles del error para ayudarte a diagnosticarlo.'
                : 'Tuvimos un inconveniente al cargar esta sección. Intentá recargar la página o volvé a intentarlo en unos momentos.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {!debugMode && (
              <p className="text-muted-foreground text-base leading-relaxed">
                Si el problema persiste, contactá al equipo de soporte e indicá el día y la hora en la que ocurrió para que podamos asistirte.
              </p>
            )}
            {debugMode && (
              <div className="space-y-6 text-left">
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-destructive">
                    {this.state.error?.name ?? 'Error inesperado'}
                  </p>
                  <p className="font-mono text-sm whitespace-pre-wrap break-words bg-muted/70 border border-border/80 rounded-lg p-4">
                    {this.state.error?.message || 'Sin mensaje disponible'}
                  </p>
                </div>
                {this.state.error?.stack && (
                  <div className="space-y-2">
                    <p className="text-sm font-semibold text-muted-foreground">Stack trace</p>
                    <pre className="font-mono text-xs whitespace-pre-wrap break-words bg-muted/50 border border-border/60 rounded-lg p-4 overflow-auto max-h-64">
                      {this.state.error.stack}
                    </pre>
                  </div>
                )}
                {this.state.errorInfo?.componentStack && (
                  <div className="space-y-2">
                    <p className="text-sm font-semibold text-muted-foreground">Componente</p>
                    <pre className="font-mono text-xs whitespace-pre-wrap break-words bg-muted/50 border border-border/60 rounded-lg p-4 overflow-auto max-h-48">
                      {this.state.errorInfo.componentStack}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </CardContent>
          <CardFooter className="flex flex-wrap items-center justify-center gap-3">
            <Button onClick={this.handleReload}>Recargar página</Button>
            <Button variant="outline" onClick={this.handleRetry}>
              Intentar de nuevo
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }
}

export default ErrorBoundary;
